import { supabase } from '../lib/supabaseClient';
import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { Navigate, useLocation } from 'react-router-dom';
import { normalizeRole, type AppRole } from '../lib/roles';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { AppRole };
export type ProfileStatus = 'pending' | 'approved' | 'rejected' | 'requires_info' | 'blocked';

type SessionContextValue = {
  session: Session | null;
  user: User | null;
  /** Role from public.profiles — null while loading or unauthenticated */
  role: AppRole | null;
  /** Approval status from public.profiles */
  profileStatus: ProfileStatus | null;
  /** True until both session AND profile role are resolved */
  loading: boolean;
  /** Force a profile refresh (call after role/status changes) */
  refreshProfile: () => Promise<void>;
  /** True when the server terminated the session (refresh token expired). */
  sessionExpired: boolean;
  /** Call after navigating away from the expiry modal. */
  clearSessionExpired: () => void;
};

// ─── Context ──────────────────────────────────────────────────────────────────

const SessionContext = createContext<SessionContextValue>({
  session: null,
  user: null,
  role: null,
  profileStatus: null,
  loading: true,
  refreshProfile: async () => {},
  sessionExpired: false,
  clearSessionExpired: () => {},
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [profileStatus, setProfileStatus] = useState<ProfileStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const prevSessionRef = useRef<Session | null>(null);

  /** Fetch role + status from profiles for the given user id */
  const fetchProfile = useCallback(async (userId: string) => {
    // Dev mode: RoleSwitcher writes __dev_nav_role__ — honour it directly
    if (import.meta.env.DEV) {
      const devRole = localStorage.getItem('__dev_nav_role__');
      if (devRole) {
        setRole(normalizeRole(devRole));
        setProfileStatus('approved');
        return;
      }
    }

    try {
      const { data } = await supabase
        .from('profiles')
        .select('role, status')
        .eq('id', userId)
        .single();

      if (data) {
        setRole(normalizeRole(data.role));
        setProfileStatus(data.status as ProfileStatus);
      }
    } catch {
      // Non-fatal — role stays null, Guard will handle the redirect
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    const { data } = await supabase.auth.getUser();
    if (data.user) await fetchProfile(data.user.id);
  }, [fetchProfile]);

  useEffect(() => {
    // Dev mode: mock session from RoleSwitcher
    if (import.meta.env.DEV) {
      const raw = localStorage.getItem('__dev_session__');
      if (raw) {
        try {
          const mock = JSON.parse(raw) as Session;
          setSession(mock);
          if (mock.user?.id) fetchProfile(mock.user.id).finally(() => setLoading(false));
          else setLoading(false);
          return;
        } catch { /* fall through */ }
      }
    }

    // Real auth: get initial session then subscribe to changes
    supabase.auth.getSession().then(({ data }) => {
      const sess = data.session ?? null;
      setSession(sess);
      if (sess?.user?.id) {
        fetchProfile(sess.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, sess) => {
      if (event === 'SIGNED_OUT' && prevSessionRef.current !== null) {
        const manual = sessionStorage.getItem('__manual_signout__');
        sessionStorage.removeItem('__manual_signout__');
        if (!manual) setSessionExpired(true);
      }
      prevSessionRef.current = sess;
      setSession(sess);
      if (sess?.user?.id) {
        fetchProfile(sess.user.id);
      } else {
        setRole(null);
        setProfileStatus(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const user = session?.user ?? null;

  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  return (
    <SessionContext.Provider value={{ session, user, role, profileStatus, loading, refreshProfile, sessionExpired, clearSessionExpired }}>
      {children}
    </SessionContext.Provider>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useSession = () => useContext(SessionContext);

// Legacy alias
export const useAuth = useSession;

// ─── RequireAuth (simple auth-only guard, no role check) ──────────────────────

export function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading) return <AuthSpinner />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

// ─── Shared spinner ───────────────────────────────────────────────────────────

export function AuthSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="space-y-4 text-center">
        <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-border border-t-primary" />
        <p className="text-sm text-muted-foreground">Indlæser…</p>
      </div>
    </div>
  );
}


